/* eslint-disable camelcase */
/* eslint-disable react/no-array-index-key */
import { FC, ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { Text, Button, Box } from '@components/design-system';
import { ISplits, IToken } from '@interface/token';
import { getURLFromIPFSURI, Node } from '@utils/ipfs';

interface IDataBlock {
  label?: string;
  children: ReactNode;
  reversed?: boolean;
}

const DataBlock: FC<IDataBlock> = ({ label, children, reversed }) => {
  return (
    <Box css={{ marginBottom: '$space$2', flexDirection: 'column' }}>
      {label && (
        <Text css={{ color: reversed ? '$secondaryFill' : '$primaryFill', fontWeight: 700, marginBottom: 8 }}>
          {label}
        </Text>
      )}
      {children}
    </Box>
  );
};

interface IInfo {
  token: IToken;
}

export const Info: FC<IInfo> = ({ token }) => {
  const t = useTranslations('token');
  const router = useRouter();
  const { locale } = router;
  const { contract } = token;

  return (
    <Box css={{ flexDirection: 'column', minHeight: 256 }}>
      {token.attributes && token.attributes.length > 0 && (
        <DataBlock label="Attributes">
          <Box
            css={{
              flexFlow: 'row wrap',
              '&>*': { marginRight: 8, marginBottom: 8 },
            }}
          >
            {token.attributes.map(({ attribute, value }, index) => {
              return (
                <Link
                  key={`token${token.token_id}-${index}`}
                  href={`/collections/${contract.name}/?${attribute.id}=${value.id}`}
                  locale={locale}
                  prefetch={false}
                  passHref
                >
                  <Box
                    as="a"
                    css={{
                      backgroundColor: '$secondaryBackground',
                      borderRadius: 5,
                      padding: '$space$4',
                      color: '$secondaryFill',
                      fontSize: 12,
                    }}
                  >
                    {attribute.name}&nbsp;
                    <Box css={{ color: '$primaryFill', fontWeight: 700, fontSize: 12 }}>{value.name}</Box>
                  </Box>
                </Link>
              );
            })}
          </Box>
        </DataBlock>
      )}

      {token.contract.wallet && (
        <DataBlock label={t('contract')}>
          <Button
            as="a"
            variant="basic"
            href={`https://tzkt.io/${token.contract.wallet.address}/operations/`}
            target="_blank"
            rel="noopener noreferrer"
            shrink
            css={{ fontSize: 16 }}
          >
            {token.contract.wallet.address}
          </Button>
        </DataBlock>
      )}

      <DataBlock label={t('symbol')}>
        <Text unset css={{ color: '$secondaryFill' }}>
          {token.symbol}
        </Text>
      </DataBlock>

      <DataBlock label={t('max-wallet')}>
        <Text unset css={{ color: '$secondaryFill' }}>
          {token.max_per_address > 0 ? token.max_per_address : 'Unlimited'}
        </Text>
      </DataBlock>

      <DataBlock label={t('royalties')}>
        <Text unset css={{ color: '$secondaryFill' }}>
          {token.royalties}%
        </Text>
      </DataBlock>

      <DataBlock label={t('splits')}>
        {token.splits
          .sort(({ dividend: a }, { dividend: b }) => b - a)
          .map((split: ISplits) => {
            return (
              <Box key={split.wallet.address}>
                <Box
                  css={{
                    margin: '0 $space$3 0 $space$3',
                    width: 16,
                    height: 16,
                    borderLeft: '2px solid $primaryBorder',
                    borderBottom: '2px solid $primaryBorder',
                    borderBottomLeftRadius: 6,
                  }}
                />
                <Box css={{ color: '$primaryFill', fontSize: 14, fontWeight: 700 }}>{split.dividend}%&nbsp;</Box>
                <Button
                  as="a"
                  variant="basic"
                  href={`https://tzkt.io/${split.wallet.address}/operations/`}
                  target="_blank"
                  rel="noopener noreferrer"
                  shrink
                >
                  <Text unset css={{ fontSize: 14, color: 'inherit' }} ellipsis>
                    {split.wallet.address}
                  </Text>
                </Button>
              </Box>
            );
          })}
      </DataBlock>

      <DataBlock label="IPFS">
        <Box
          css={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Button
            as="a"
            variant="basic"
            href={getURLFromIPFSURI(token.metadata_uri, Node.IPFS)}
            target="_blank"
            rel="noopener noreferrer"
            shrink
            css={{ fontSize: 16 }}
          >
            Metadata
          </Button>

          <Button
            as="a"
            variant="basic"
            href={getURLFromIPFSURI(token.artifact_uri, Node.IPFS)}
            target="_blank"
            rel="noopener noreferrer"
            shrink
            css={{ fontSize: 16 }}
          >
            Artifact
          </Button>
        </Box>
      </DataBlock>
    </Box>
  );
};
